import React, { useCallback, useState } from 'react';
import { Badge, IconButton, Popover, Tab, Tabs, Tooltip } from '@mui/material';
import { Bell, Settings } from 'lucide-react';
import { Link } from 'react-router';
import { useI18n } from '../../../i18n/I18nProvider';
import { useNotificationTab } from '../../../app/layout/notificationTabPreference';
import { useMenuTabsIndicator } from '../../../app/layout/menuTabsIndicator';
import { useNotifications } from '../useNotifications';
import { useNotificationOpen } from '../useNotificationOpen';
import type { NotificationItem } from '../api';
import NotificationList from './NotificationList';
import LiveNotificationToast, { LIVE_TYPES } from './LiveNotificationToast';
import './NotificationBell.css';

/**
 * The bell in the header: the unread badge, the panel (All / Unread / Needs you), and the live
 * pop-up for notifications that arrive while the app is open.
 *
 * A Popover rather than a Menu: rows hold several buttons (open, its actions, dismiss), which a
 * MenuList's arrow-key model cannot reach, so rows were unreachable from the keyboard. Here
 * everything is in the normal Tab order.
 */
export default function NotificationBell() {
  const { t } = useI18n();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [tab, selectTab] = useNotificationTab();
  const indicator = useMenuTabsIndicator();
  const [live, setLive] = useState<NotificationItem | null>(null);
  const open = Boolean(anchor);

  const onArrived = useCallback((item: NotificationItem) => {
    if (LIVE_TYPES.has(item.type)) setLive(item);
  }, []);

  const n = useNotifications({ tab, active: open, onArrived });
  const close = useCallback(() => setAnchor(null), []);
  const { open: openItem, act } = useNotificationOpen({ markRead: n.markRead, runAction: n.runAction, onNavigate: close });

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
    // The badge may have gone stale while the socket was down; opening is a good moment to ask.
    n.refreshCounts();
  };

  const empty = tab === 'unread'
    ? t('notifications.emptyUnread')
    : tab === 'needs_you' ? t('notifications.emptyNeedsYou') : t('notifications.empty');

  return (
    <>
      <Tooltip title={t('notifications.title')}>
        <IconButton
          onClick={handleOpen}
          className="app-shell__header-action"
          size="small"
          aria-label={n.counts.unread > 0
            ? t('notifications.bellLabelUnread', { count: n.counts.unread })
            : t('notifications.title')}
          aria-haspopup="dialog"
          aria-expanded={open}
        >
          {/* Purple, not red: red is for errors (spec 4.2). Styled in App.css. */}
          <Badge badgeContent={n.counts.unread > 0 ? n.counts.unread : null} color="primary" max={99}>
            <Bell size={20} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        disableScrollLock
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: (theme) => theme.zIndex.appBar + 10 }}
        slotProps={{
          paper: {
            className: 'notif-panel',
            role: 'dialog',
            'aria-label': t('notifications.title'),
          } as any,
          transition: { onEnter: indicator.handleEnter, onEntered: indicator.handleEntered },
        }}
      >
        <div className="notif-panel__head">
          <h2 className="notif-panel__title">{t('notifications.title')}</h2>
          <div className="notif-panel__head-actions">
            {n.counts.unread > 0 && (
              <button type="button" className="notif-panel__link" onClick={n.markAllRead}>
                {t('notifications.markAllRead')}
              </button>
            )}
            <Link
              to="/settings/notifications"
              onClick={close}
              className="notif-panel__icon-link"
              aria-label={t('notificationSettings.manageLink')}
            >
              <Settings size={14} />
            </Link>
          </div>
        </div>

        <Tabs
          value={tab}
          onChange={(_, v) => selectTab(v)}
          action={indicator.action}
          className="notif-panel__tabs"
          sx={{
            px: 1,
            minHeight: 36,
            flexShrink: 0,
            '& .MuiTab-root': { minHeight: 40, fontSize: 'var(--font-size-13)', py: 0.5, px: 1.5, textTransform: 'none' },
            ...indicator.indicatorSx,
          }}
        >
          <Tab label={t('notifications.tabAll')} value="all" />
          <Tab label={n.counts.unread > 0 ? t('notifications.tabUnreadCount', { count: n.counts.unread }) : t('notifications.tabUnread')} value="unread" />
          <Tab label={n.counts.needs_you > 0 ? t('notifications.tabNeedsYouCount', { count: n.counts.needs_you }) : t('notifications.tabNeedsYou')} value="needs_you" />
        </Tabs>

        <div className="notif-panel__list">
          {n.loading && n.items.length === 0 ? (
            <p className="notif-panel__loading">{t('common.loading')}</p>
          ) : (
            <NotificationList
              items={n.items}
              emptyText={empty}
              onOpen={openItem}
              onAction={act}
              onDismiss={n.dismiss}
            />
          )}
          {n.next && (
            <button type="button" className="notif-panel__more" onClick={n.loadMore} disabled={n.loadingMore}>
              {n.loadingMore ? t('common.loading') : t('notifications.loadMore')}
            </button>
          )}
        </div>

        <div className="notif-panel__foot">
          <Link to="/notifications" onClick={close} className="notif-panel__link">
            {t('notifications.seeAll')}
          </Link>
          {n.hasRead && (
            <button type="button" className="notif-panel__link notif-panel__link--muted" onClick={n.clearRead}>
              {t('notifications.clearRead')}
            </button>
          )}
        </div>
      </Popover>

      <LiveNotificationToast
        item={live}
        onAction={act}
        onOpen={(item) => openItem(item)}
        onClose={() => setLive(null)}
      />
    </>
  );
}
